import { SORT_ICON_NONE } from '@appViews/shared/sortIcon';
import CollectionStatusBadge from '@boViews/shared/CollectionStatusBadge';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import Action from '@sharedComponents/Action';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { formatDateTime } from '@utils/date';

import ClientsCell from '../ClientsCell';

import * as style from './style.module.css';
import rowStyle from '@sharedComponents/Row/style.module.css';

const EMPTY = '—';
// Longest domain label shown in a badge, ellipsis included; the full label goes to a tooltip.
const DOMAIN_LABEL_MAX_LENGTH = 30;
// Room for a badge at the label limit (0.6 ≈ average glyph width / font size), so the column keeps
// the same width from one page to the next while the name column takes the remaining space.
const DOMAIN_CELL_STYLE = { minWidth: `calc(${DOMAIN_LABEL_MAX_LENGTH} * 0.6 * var(--font-xs) + 2 * var(--space-xs))` };

// Keyed by the French typology label: a renamed typology falls back to the neutral colour.
const TYPOLOGY_COLORS = {
    'Savoir faire': 'lavender',
    'Spécifique Client': 'error',
    'Aptitudes générales': 'teal',
    'Savoir-être': 'teal',
    Education: 'success',
    Langues: 'success',
    'Prévention et sécurité': 'lavenderStrong',
    'Prévention des risques professionnels': 'lavenderStrong',
};

// First typology (by id) whose label is mapped wins; none mapped means neutral.
const getTypologyColor = (tags) => {
    const typologies = tags.filter((tag) => tag.type === 'typology').sort((a, b) => a.id - b.id);
    const mapped = typologies.find((tag) => TYPOLOGY_COLORS[tag.label]);
    return mapped ? TYPOLOGY_COLORS[mapped.label] : 'neutral';
};

const CollectionNameCell = ({ collection, t, onOpen }) => (
    <FlexBox flexDirection="column" gap="xxs">
        <Action action={() => onOpen(collection)} tabIndex={0} role="link">
            <Typography span fontWeight="medium">
                {collection.name}
            </Typography>
        </Action>
        <Typography fontSize="s" color="neutral-tertiary">
            {t('{{count}} test(s)', { count: collection.testReferences.length })}
        </Typography>
    </FlexBox>
);

// Truncated in JS rather than by Tag's CSS: the limit is a character count, and the tooltip must
// wrap the whole badge (Tag renders its text in a <p>, which cannot hold the tooltip's <div>).
const DomainBadge = ({ label, color }) => {
    const isTruncated = label.length > DOMAIN_LABEL_MAX_LENGTH;
    const shownLabel = isTruncated ? `${label.slice(0, DOMAIN_LABEL_MAX_LENGTH - 1).trimEnd()}…` : label;

    return (
        <Tooltip text={label} popupIcon={false} position="top" display={isTruncated}>
            <Tag color={color} size="badge" fontSize="xs" noTruncate className={style.domainBadge}>
                {shownLabel}
            </Tag>
        </Tooltip>
    );
};

const DomainCell = ({ collection }) => {
    const domains = collection.tags.filter((tag) => tag.type === 'domain');
    if (domains.length === 0) return EMPTY;
    const color = getTypologyColor(collection.tags);

    return (
        <FlexBox gap="xxs" additionnalStyles={DOMAIN_CELL_STYLE}>
            {domains.map((tag) => (
                <DomainBadge key={tag.id} label={tag.label} color={color} />
            ))}
        </FlexBox>
    );
};

// The name and the pencil of a row both open the page of the collection (`onOpen`).
export const getCollectionColumns = (t, { onOpen = () => {}, sortBy = '', sortIcon } = {}) => {
    const activeSortKey = sortBy.replace(/^-/, '');
    const iconFor = (key) => (activeSortKey === key ? sortIcon : SORT_ICON_NONE);

    return [
        {
            key: 'name',
            label: t('Name'),
            icon: iconFor('name'),
            // Sort column: dropping it would hide what the list is ordered by. Also carries `fill`,
            // which only applies while the column is visible.
            pinned: true,
            // Takes the leftover width; the other columns shrink to their bounded content.
            fill: true,
            tdClassName: rowStyle.td,
            render: (collection) => <CollectionNameCell collection={collection} t={t} onOpen={onOpen} />,
        },
        {
            key: 'clients',
            label: t('Clients'),
            tdClassName: rowStyle.td,
            render: (collection) => <ClientsCell collection={collection} />,
        },
        {
            key: 'domain',
            label: t('Subdomain'),
            tdClassName: rowStyle.td,
            render: (collection) => <DomainCell collection={collection} />,
        },
        {
            key: 'status',
            label: t('Status'),
            tdClassName: rowStyle.td,
            render: (collection) => <CollectionStatusBadge status={collection.status} />,
        },
        {
            key: 'updatedAt',
            label: t('Last modified'),
            icon: iconFor('updatedAt'),
            pinned: true,
            tdClassName: rowStyle.td,
            render: (collection) => (collection.updatedAt ? formatDateTime(collection.updatedAt) : EMPTY),
        },
        {
            key: 'actions',
            ariaLabel: t('Actions'),
            pinned: true,
            tdClassName: rowStyle.tdActions,
            render: (collection) => <Button ghost icon={faPenToSquare} title={t('Edit')} action={() => onOpen(collection)} />,
        },
    ];
};
